import { getApiClient } from "@src/api";
import { graphql } from "@src/gql/gql";
import type {
	GetSupportedAgentsQuery,
	SupportedAgent,
	SupportedModel,
} from "@src/gql/graphql";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { supportedAgentsQueryKeys } from "./query-keys";

const getSupportedAgentsQuery = graphql(/* GraphQL */ `
	query GetSupportedAgents {
		ai {
			allAgents {
				agent
				models {
					model
					modelId
					isRegionRestricted
					isTierAvailable
					tier
					compatibleAgents
					capabilities
					maxInputTokens
					maxImages
					inputModalities
					outputModalities
				}
			}
			myAgents {
				agent
				models {
					model
					modelId
					isRegionRestricted
					isTierAvailable
					tier
					compatibleAgents
					capabilities
					maxInputTokens
					maxImages
					inputModalities
					outputModalities
				}
				hasCustomPreferences
				defaultModels {
					isRegionRestricted
					model
					modelId
					isTierAvailable
					tier
					compatibleAgents
					capabilities
					maxInputTokens
					maxImages
					inputModalities
					outputModalities
				}
			}
		}
	}
`);

export type AllAgent = GetSupportedAgentsQuery["ai"]["allAgents"][number];
export type MyAgent = GetSupportedAgentsQuery["ai"]["myAgents"][number];

/** Shape stored under {@link supportedAgentsQueryKeys.all}. */
export type SupportedAgentsQueryData = {
	allAgents: AllAgent[];
	myAgents: MyAgent[];
};

/**
 * Returns next cache value with one agent's preferred models replaced (for
 * optimistic updates). Only models present in allAgents are kept so we never
 * invent incomplete metadata.
 */
export function patchMyAgentPreferredModels(
	prev: SupportedAgentsQueryData | undefined,
	agentId: SupportedAgent,
	newModelIds: SupportedModel[],
): SupportedAgentsQueryData | undefined {
	if (!prev) return prev;
	const agentAllModels =
		prev.allAgents.find((a) => a.agent === agentId)?.models ?? [];
	const updatedModels = newModelIds.flatMap((m) => {
		const meta = agentAllModels.find((am) => am.model === m);
		return meta ? [meta] : [];
	});
	return {
		...prev,
		myAgents: prev.myAgents.map((a) =>
			a.agent === agentId
				? { ...a, models: updatedModels, hasCustomPreferences: true }
				: a,
		),
	};
}

/**
 * Returns next cache value with every agent's preferred models restored to
 * defaults (for optimistic reset).
 */
export function resetMyAgentsPreferredModels(
	prev: SupportedAgentsQueryData | undefined,
): SupportedAgentsQueryData | undefined {
	if (!prev) return prev;
	return {
		...prev,
		myAgents: prev.myAgents.map((a) => ({
			...a,
			models: a.defaultModels,
			hasCustomPreferences: false,
		})),
	};
}

async function getSupportedAgents() {
	const client = getApiClient();
	const response = await client.query({
		query: getSupportedAgentsQuery,
	});

	const { allAgents, myAgents } = response.data.ai;
	return { allAgents, myAgents };
}

export const supportedAgentsQueryOptions = queryOptions({
	queryKey: supportedAgentsQueryKeys.all,
	queryFn: getSupportedAgents,
	staleTime: 5 * 60 * 1000,
});

export function useGetSupportedAgents() {
	return useQuery(supportedAgentsQueryOptions);
}
